'use client';

import * as React from 'react';
import { useEffectOnce } from 'react-use';

type AuthContextData = {
  notifyChangedProxy: () => void;
};

const AuthContext = React.createContext({} as AuthContextData);

let authChannel: BroadcastChannel;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  useEffectOnce(() => {
    function handleIncomingMessage(e: MessageEvent) {
      if (e.data === 'changedProxy') {
        window.location.reload();
      }
    }

    authChannel = new BroadcastChannel('@ghq/auth');
    authChannel.addEventListener('message', handleIncomingMessage);

    return () => {
      authChannel.removeEventListener('message', handleIncomingMessage);
    };
  });

  const notifyChangedProxy = React.useCallback(() => {
    authChannel?.postMessage('changedProxy');
  }, []);

  const providerValue = React.useMemo(
    () => ({ notifyChangedProxy }),
    [notifyChangedProxy]
  );

  return (
    <AuthContext.Provider value={providerValue}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const context = React.useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
